import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@headlessui/react'
import { Button, Text, Card } from '@tremor/react'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { sanitizeUserInput } from '../../utils/security'

function ResearchQuestionFormModal({ isOpen, onClose, onSubmit, language }) {
    const { t } = useTranslation()
    const [currentStep, setCurrentStep] = useState(1)
    const [formResponses, setFormResponses] = useState({})
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [estimatedCredits, setEstimatedCredits] = useState(null)

    // Research questions in all three languages
    const questions = {
        en: [
            "What is the central phenomenon I intend to study?",
            "What is the underlying scientific problem?", 
            "What is the population or unit of analysis?",
            "What is the context of the study?",
            "What are the main concepts involved?",
            "What relationship do I intend to analyze?",
            "What is the central objective of the study?",
            "Is the question clear, specific, investigatable, delimited, and non-normative?",
            "What type of methodology will be coherent with the question?",
            "What scientific contribution do I expect to produce?"
        ],
        es: [
            "¿Cuál es el fenómeno central que pretendo estudiar?",
            "¿Cuál es el problema científico subyacente?",
            "¿Cuál es la población o unidad de análisis?", 
            "¿Cuál es el contexto del estudio?",
            "¿Cuáles son los principales conceptos involucrados?",
            "¿Qué relación pretendo analizar?",
            "¿Cuál es el objetivo central del estudio?",
            "¿La pregunta es clara, específica, investigable, delimitada y no normativa?",
            "¿Qué tipo de metodología será coherente con la pregunta?",
            "¿Qué contribución científica espero producir?"
        ],
        pt: [
            "Qual é o fenómeno central que pretendo estudar?",
            "Qual é o problema científico subjacente?",
            "Qual é a população ou unidade de análise?",
            "Qual é o contexto do estudo?",
            "Quais são os principais conceitos envolvidos?",
            "Que relação pretendo analisar?",
            "Qual é o objetivo central do estudo?",
            "A questão é clara, específica, investigável, delimitada e não normativa?",
            "Que tipo de metodologia será coerente com a questão?",
            "Que contributo científico espero produzir?"
        ]
    }

    const currentQuestions = questions[language] || questions.en

    // Check if form has minimum responses to generate
    const validResponses = Object.values(formResponses).filter(response => response && response.trim().length >= 5)
    const canGenerate = validResponses.length >= 3

    // Simple function to estimate credits based on content length
    const estimateCreditsForGeneration = () => {
        if (validResponses.length < 3) return null
        
        // Calculate total content length
        const totalLength = validResponses.reduce((sum, response) => sum + (response?.length || 0), 0)
        
        // Rough estimation: 
        // - Base cost for AI processing: 0.5 credits
        // - Additional cost based on content length: 1 credit per ~500 characters
        // - Generate 3 suggestions: multiply by 1.5
        const baseCredits = 0.5
        const contentCredits = Math.ceil(totalLength / 500) * 0.3
        const totalEstimate = Math.ceil((baseCredits + contentCredits) * 1.5)
        
        // Minimum 1 credit, maximum 5 credits for display
        return Math.max(1, Math.min(5, totalEstimate))
    }

    // Update credit estimate when form has minimum responses
    React.useEffect(() => {
        if (canGenerate) {
            setEstimatedCredits(estimateCreditsForGeneration())
        } else {
            setEstimatedCredits(null)
        }
    }, [canGenerate, formResponses])

    const handleInputChange = (value) => {
        setFormResponses(prev => ({
            ...prev,
            [currentStep]: value
        }))
    }

    const handleNext = () => {
        const currentResponse = formResponses[currentStep]
        if (!currentResponse || currentResponse.trim().length < 5) {
            setError(t('Research.QuestaoPartida.Form.ResponseTooShort'))
            return
        }
        
        setError(null)
        if (currentStep < 10) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handlePrev = () => {
        setError(null)
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1)
        }
    }

    const handleSkip = () => {
        setError(null)
        // Move to next question without requiring input
        if (currentStep < 10) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleSubmit = async () => {
        // Only include responses that have content (allow skipped questions)
        const responses = Object.keys(formResponses)
            .filter(step => formResponses[step] && formResponses[step].trim().length >= 5)
            .map(step => ({
                question_number: parseInt(step),
                question_text: currentQuestions[parseInt(step) - 1],
                user_response: sanitizeUserInput(formResponses[step])
            }))

        // Require at least 3 answered questions for generation
        if (responses.length < 3) {
            setError(t('Research.QuestaoPartida.Form.MinimumResponses'))
            return
        }

        try {
            setLoading(true)
            setError(null)
            await onSubmit(responses)
        } catch (err) {
            setError(err.message || t('Research.QuestaoPartida.Form.SubmitError'))
        } finally {
            setLoading(false)
        }
    }

    const progress = (Object.keys(formResponses).length / 10) * 100

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="w-full h-full bg-white flex flex-col">
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">
                                {t('Research.QuestaoPartida.Form.Title')}
                            </Dialog.Title>
                            <Text className="text-gray-600 mt-1">
                                {t('Research.QuestaoPartida.Form.Description')}
                            </Text>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500"
                        >
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    {/* Main scrollable content */}
                    <div className="flex-1 overflow-y-auto">

                        <div className="p-8 max-w-4xl mx-auto h-full flex flex-col">
                            <div className="flex-1 flex flex-col justify-center">
                                <div className="mb-12">
                                    <h2 className="text-2xl md:text-3xl font-light text-gray-900 mb-8 leading-tight text-left">
                                        {currentQuestions[currentStep - 1]}
                                    </h2>
                                    
                                    <div>
                                        <input
                                            type="text"
                                            value={formResponses[currentStep] || ''}
                                            onChange={(e) => handleInputChange(e.target.value)}
                                            placeholder={t('Research.QuestaoPartida.Form.ResponsePlaceholder')}
                                            className="w-full text-lg p-4 bg-transparent border-0 border-b-2 border-gray-300 focus:border-iavisus-100 focus:outline-none focus:ring-0 transition-colors"
                                            maxLength={500}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Bottom section with error and buttons */}
                            <div className="border-t border-gray-100 pt-6 mt-auto">
                                {error && (
                                    <div className="mb-4 text-center">
                                        <Text className="text-red-600 text-sm">{error}</Text>
                                    </div>
                                )}

                                <div className="flex justify-between items-center">
                            <Button
                                variant="secondary"
                                onClick={handlePrev}
                                disabled={currentStep === 1}
                                icon={ChevronLeftIcon}
                                className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
                            >
                                {t('Common.Previous')}
                            </Button>

                            <div className="flex space-x-3">
                                <Button
                                    variant="secondary"
                                    onClick={handleSkip}
                                    className="bg-gray-100 text-gray-700 hover:bg-gray-200"
                                >
                                    {t('Common.Skip')}
                                </Button>

                                {currentStep < 10 ? (
                                    <Button
                                        onClick={handleNext}
                                        icon={ChevronRightIcon}
                                        iconPosition="right"
                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200"
                                    >
                                        {t('Common.Next')}
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={handleSubmit}
                                        icon={SparklesIcon}
                                        disabled={loading}
                                        className="bg-iavisus-100 text-white hover:bg-iavisus-200 disabled:opacity-50"
                                    >
                                        {loading 
                                            ? t('Common.Generating') 
                                            : estimatedCredits 
                                                ? `${t('Research.QuestaoPartida.Form.GenerateQuestions')} (${estimatedCredits} ${t('Common.Credits')})`
                                                : t('Research.QuestaoPartida.Form.GenerateQuestions')
                                        }
                                    </Button>
                                )}
                            </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    )
}

export default ResearchQuestionFormModal